"use client";

import * as React from "react";
import { useTheme } from "next-themes";
import { Moon, Sun } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Unobtrusive light/dark theme toggle for the site footer. Renders a small
 * icon button that cycles between light and dark; intended for use next to
 * the version number when the user is not logged in.
 */
export function FooterThemeToggle(): React.ReactElement {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Hydration safety: `resolvedTheme` is client-derived; avoid SSR/client mismatches.
  const isDark = mounted && resolvedTheme === "dark";

  function handleClick(): void {
    if (!mounted) return;
    setTheme(isDark ? "light" : "dark");
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Toggle light/dark theme"
      disabled={!mounted}
      className={cn(
        "relative inline-flex items-center justify-center rounded p-1 text-muted-foreground",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        !mounted && "pointer-events-none opacity-70"
      )}
    >
      <span className="relative inline-block h-3.5 w-3.5">
        <Sun className="h-3.5 w-3.5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute inset-0 h-3.5 w-3.5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      </span>
    </button>
  );
}
